import { useDraggable, useDroppable } from "@dnd-kit/core";
import { assignRefs, cx } from "@jugl-web/utils";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { Resizable } from "react-resizable";
import { TableSortingButton } from "../../../TableSortingButton";
import { TableGridColumn } from "../../types";
import { ReactComponent as ReorderIcon } from "../../assets/reorder.svg";
import { useResizableColumn } from "../../useResizableColumn";

interface TableThProps<TData> {
  [key: `data-table-${string}`]: string;
  id: string;
  idx: number;
  col: TableGridColumn<TData>;
  currentWidth?: number;
  className?: string;
  style?: CSSProperties;
  onColumnResize?: (columnId: string, newWidth: number) => void;
}

export const TableTh = <TData,>({
  id,
  idx,
  currentWidth,
  col,
  className,
  style,
  onColumnResize,
  ...props
}: TableThProps<TData>) => {
  const [isInteractivityDisabled, setIsInteractivityDisabled] = useState(false);

  const isDraggable = !!col.isDraggable;

  const { setNodeRef, setActivatorNodeRef, attributes, listeners } =
    useDraggable({
      id,
      disabled: !isDraggable,
    });

  const { setNodeRef: setNodeRefDroppable, isOver } = useDroppable({
    id,
    disabled: !isDraggable,
  });

  const { isResizing, resizableProps, handleClassName, resizeOffset } =
    useResizableColumn({
      onResizeCommit: useCallback(
        (width: number) => onColumnResize?.(col.id, width),
        [col.id, onColumnResize]
      ),
      width: currentWidth ?? 150,
      minWidth: col.resizeOptions?.min,
      maxWidth: col.resizeOptions?.max,
    });

  const isInteractive = col.onHeaderClick || col.sortingOptions?.isEnabled;
  const isResizable = col.resizeOptions?.isEnabled;

  const handleClick = () => {
    if (isInteractivityDisabled) {
      return;
    }

    col.onHeaderClick?.();

    if (col.sortingOptions?.isEnabled && !col.sortingOptions?.isMenu) {
      col.sortingOptions?.onSort(col.sortingOptions.sortOrder);
    }
  };

  // Disable interactivity when resizing to prevent unexpected header clicks
  useEffect(() => {
    let timeoutId: number | null = null;

    if (isResizing) {
      setIsInteractivityDisabled(true);
    } else {
      timeoutId = window.setTimeout(() => {
        setIsInteractivityDisabled(false);
      }, 100);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isResizing]);

  const titleText = typeof col.title === "string" ? col.title : undefined;

  const headingCell = (
    <div
      key={+idx}
      ref={assignRefs([setNodeRef, setNodeRefDroppable])}
      className={cx(
        "group",
        className,
        isInteractive && "cursor-pointer",
        isOver && "border border-solid border-grey-background bg-grey-200"
      )}
      style={style}
      onClick={handleClick}
      {...props}
    >
      <div className="flex w-full items-center justify-between gap-2">
        {isDraggable ? (
          <div
            ref={setActivatorNodeRef}
            className="flex cursor-grab items-center gap-2 overflow-hidden select-none"
            {...attributes}
            {...listeners}
          >
            <ReorderIcon className="shrink-0" />
            <span className="truncate" title={titleText}>
              {col.title}
            </span>
          </div>
        ) : (
          <span className="truncate" title={titleText}>
            {col.title}
          </span>
        )}

        {col.sortingOptions?.isEnabled && (
          <TableSortingButton
            sortingType={col.sortingOptions.sortingType}
            sortingOrder={
              col.sortingOptions.isActive ? col.sortingOptions.sortOrder : null
            }
            onSortSelect={(sortingOptions: "asc" | "desc" | null) =>
              col.sortingOptions?.onSortSelect?.(sortingOptions)
            }
            isMenu={col.sortingOptions.isMenu}
            className={cx(
              "transition-all duration-200",
              !col.sortingOptions.isActive
                ? "pointer-events-none translate-x-4 opacity-0 group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100"
                : ""
            )}
          />
        )}
      </div>
    </div>
  );

  if (!isResizable) {
    return headingCell;
  }

  return (
    <Resizable
      {...resizableProps}
      handle={
        <div
          className={handleClassName}
          style={
            isResizing
              ? { transform: `translateX(${resizeOffset}px)` }
              : undefined
          }
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        />
      }
    >
      {headingCell}
    </Resizable>
  );
};
